import { useQuery } from '@tanstack/react-query';
import { translationAPI, recipeAPI } from '@/utils/api';

/**
 * useRecipe — React Query hook for fetching recipe data.
 * Caches per [slug, lang] with 10-minute staleTime.
 * 
 * HARDENED: Always returns valid recipe object or throws error.
 * Never returns undefined/malformed data.
 */
export function useRecipe(slug, lang = 'en') {
  return useQuery({
    queryKey: ['recipe', slug, lang],
    queryFn: async () => {
      // Step 1: Try pre-translated content (fast)
      try {
        const translationRes = await translationAPI.getRecipe(slug, lang);
        
        // Validate response exists
        if (!translationRes || !translationRes.data) {
          throw new Error('Invalid translation response');
        }
        
        const td = translationRes.data;

        if (td.status === 'ready' && td.content) {
          const recipe = { ...td.content, ...td.metadata, slug: td.slug };
          
          // Validate recipe has minimum required fields
          if (!recipe.recipe_name && !recipe.title_original) {
            throw new Error('Recipe missing required name field');
          }
          
          return {
            recipe,
            contentLanguage: td.lang,
            translationStatus: 'ready',
          };
        }
      } catch (translationError) {
        // Log but continue to fallback - don't throw here
        console.warn('Translation fetch failed, trying direct fetch:', translationError?.message);
      }

      // Step 2: Fetch with on-the-fly translation
      const res = await recipeAPI.getBySlug(slug, lang);
      
      // CRITICAL: Validate API response
      if (!res || !res.data) {
        throw new Error('Invalid recipe response from API');
      }
      
      const data = res.data;
      
      // Validate recipe has minimum required data
      if (!data.recipe_name && !data.title_original && !data.slug) {
        throw new Error('Recipe data is incomplete or malformed');
      }
      
      const displayLang = (data._display_lang || data.content_language || 'en').slice(0, 2).toLowerCase();
      const isMatch = displayLang === lang || data._translated;

      return {
        recipe: data,
        contentLanguage: displayLang,
        translationStatus: isMatch ? 'ready' : 'fallback',
      };
    },
    enabled: !!slug,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
  });
}
